// frontend/src/components/client/ClientProfile.jsx
import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, MapPin, Plane, Bell, MessageCircle, Save, CheckCircle } from 'lucide-react';
import { getProfile, getMyAircraft, getNotificationPreferences, updateNotificationPreferences } from '../../services/clientApi';

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
    <Icon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-white break-words">{value || 'Not specified'}</p>
    </div>
  </div>
);

const ClientProfile = () => {
  const [profile, setProfile] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [telegramNotifications, setTelegramNotifications] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, aircraftRes, notifRes] = await Promise.all([
        getProfile(),
        getMyAircraft(),
        getNotificationPreferences()
      ]);
      setProfile(profileRes.data);
      setAircraft(aircraftRes.data);
      
      // Load notification preferences
      if (notifRes.data) {
        setEmailNotifications(notifRes.data.email_notifications !== false);
        setSmsNotifications(notifRes.data.sms_notifications === true);
        setTelegramNotifications(notifRes.data.telegram_notifications === true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      await updateNotificationPreferences({
        email_notifications: emailNotifications,
        sms_notifications: smsNotifications,
        telegram_notifications: telegramNotifications
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-0 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">My Profile</h2>
        <p className="text-gray-400 text-sm">View your personal information and notification settings</p>
      </div>

      {/* Profile Card - Lecture seule */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6">
          {/* Company / Basic Info */}
          <div className="mb-6 pb-6 border-b border-gray-700">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Building className="w-4 h-4 text-blue-400" />
              Company Information
            </h3>
            <InfoRow icon={Building} label="Company" value={profile?.company_name} />
            <InfoRow icon={Mail} label="Email" value={profile?.email} />
            <InfoRow icon={User} label="Full Name" value={`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()} />
          </div>

          {/* Contact Information */}
          <div className="mb-6 pb-6 border-b border-gray-700">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-400" />
              Contact Information
            </h3>
            <InfoRow icon={Phone} label="Phone" value={profile?.phone} />
            <InfoRow icon={MapPin} label="Address" value={profile?.address} />
            {profile?.city && (
              <InfoRow icon={MapPin} label="City/State/Zip" value={`${profile?.city || ''} ${profile?.state || ''} ${profile?.zip_code || ''}`.trim()} />
            )}
          </div>

          {/* Notification Preferences */}
          <div className="mb-6 pb-6 border-b border-gray-700">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-400" />
              Notification Preferences
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Choose how you want to receive notifications about your fuel and maintenance requests
            </p>
            
            <div className="space-y-3">
              {/* Email Notifications */}
              <label className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-900/70 transition">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div>
                    <span className="text-white">Email Notifications</span>
                    <p className="text-xs text-gray-500">Receive updates via email at {profile?.email || 'your email'}</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                />
              </label>

              {/* SMS Notifications */}
              <label className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-900/70 transition">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-green-400" />
                  <div>
                    <span className="text-white">SMS Notifications</span>
                    <p className="text-xs text-gray-500">
                      Receive updates via text message
                      {profile?.phone ? ` at ${profile.phone}` : ' (no phone number on file)'}
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={smsNotifications}
                  onChange={(e) => setSmsNotifications(e.target.checked)}
                  disabled={!profile?.phone}
                  className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500 disabled:opacity-50"
                />
              </label>

              {/* Telegram Notifications */}
              <label className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg cursor-pointer hover:bg-gray-900/70 transition">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-5 h-5 text-sky-400" />
                  <div>
                    <span className="text-white">Telegram Notifications</span>
                    <p className="text-xs text-gray-500">
                      Receive updates via Telegram
                      {profile?.telegram_username ? ` @${profile.telegram_username}` : ' (no Telegram username on file)'}
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={telegramNotifications}
                  onChange={(e) => setTelegramNotifications(e.target.checked)}
                  disabled={!profile?.telegram_username}
                  className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500 disabled:opacity-50"
                />
              </label>
            </div>

            {/* Save Button */}
            <div className="mt-4 flex items-center justify-end gap-3">
              {saveSuccess && (
                <span className="flex items-center gap-1 text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Preferences saved!
                </span>
              )}
              <button
                onClick={savePreferences}
                disabled={saving}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>

          {/* Aircraft Section */}
          <div>
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Plane className="w-4 h-4 text-blue-400" />
              My Aircraft ({aircraft.length})
            </h3>
            {aircraft.length === 0 ? (
              <p className="text-gray-500 text-sm">No aircraft registered</p>
            ) : (
              <div className="space-y-2">
                {aircraft.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{a.tail_number}</p>
                      <p className="text-xs text-gray-500">{a.manufacturer} {a.model} ({a.year})</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {a.status || 'Active'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message pour le client */}
          <div className="mt-6 pt-4 border-t border-gray-700 text-center">
            <p className="text-xs text-gray-500">
              For any changes to your profile information, please contact your FBO administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;